import DOMPurify from 'dompurify';

export const sanitizeTwitchUsername = (username) => {
  if (!username || typeof username !== 'string') return '';
  return username.replace(/[^a-zA-Z0-9_]/g, '').slice(0, 25);
};

export const buildTwitchEmbedUrl = (channel, parent, autoplay = false) => {
  const safeChannel = sanitizeTwitchUsername(channel);
  const safeParent = (parent || '').replace(/[^a-zA-Z0-9.-]/g, '');
  if (!safeChannel || !safeParent) return null;
  const url = `https://player.twitch.tv/?channel=${safeChannel}&parent=${safeParent}&autoplay=${autoplay ? 'true' : 'false'}`;
  return DOMPurify.sanitize(url);
};

const ALLOWED_IMAGE_DOMAINS = [
  'static-cdn.jtvnw.net',
  'clips-media-assets2.twitch.tv',
  'vod-secure.twitch.tv',
  'vod-metro.twitch.tv',
  'cdn.supabase.co',
  'blackgamersnetwork.com',
  'vercel.app',
  'ngrok-free.app'
];

// Returns the sanitized URL, or null if invalid/untrusted.
// Callers that need a fallback (e.g. avatar images) should do:
//   src={sanitizeImageUrl(url) ?? '/images/default-avatar.png'}
export const sanitizeImageUrl = (url) => {
  if (!url || typeof url !== 'string') return null;

  try {
    const parsed = new URL(url);
    // Only allow https (never http — prevents mixed content and MITM)
    if (parsed.protocol !== 'https:') return null;
    const isDomainAllowed = ALLOWED_IMAGE_DOMAINS.some(domain =>
      parsed.hostname === domain || parsed.hostname.endsWith(`.${domain}`)
    );
    if (!isDomainAllowed) return null;
    return DOMPurify.sanitize(url);
  } catch {
    return null;
  }
};

export const sanitizeText = (text) => {
  if (!text || typeof text !== 'string') return '';
  return DOMPurify.sanitize(text, { ALLOWED_TAGS: [] });
};

export const sanitizeHtml = (html) => {
  if (!html || typeof html !== 'string') return '';
  return DOMPurify.sanitize(html, {
    ALLOWED_TAGS: ['b', 'i', 'em', 'strong', 'a', 'p', 'br', 'ul', 'ol', 'li'],
    ALLOWED_ATTR: ['href', 'target', 'rel'],
    ALLOWED_URI_REGEXP: /^https:/i,
    FORCE_BODY: true,
    FORBID_ATTR: ['style', 'onerror', 'onload', 'onclick'],
  });
};

export const safeUrl = (url) => {
  if (!url || typeof url !== 'string') return null;
  if (url.startsWith('/')) return url;
  try {
    const parsed = new URL(url);
    if (parsed.protocol !== 'https:' && parsed.protocol !== 'http:') return null;
    return DOMPurify.sanitize(url);
  } catch { return null; }
};

// For window.open / navigation — domain-allowlisted to prevent Open Redirect
const ALLOWED_EXTERNAL_DOMAINS = [
  'twitch.tv',
  'youtube.com',
  'twitter.com',
  'x.com',
  'instagram.com',
  'tiktok.com',
  'discord.gg',
  'discord.com',
  'facebook.com',
];

export const safeExternalUrl = (url) => {
  if (!url || typeof url !== 'string') return null;
  try {
    const parsed = new URL(url);
    if (parsed.protocol !== 'https:') return null;
    const isAllowed = ALLOWED_EXTERNAL_DOMAINS.some(domain =>
      parsed.hostname === domain || parsed.hostname.endsWith(`.${domain}`)
    );
    if (!isAllowed) return null;
    return DOMPurify.sanitize(url);
  } catch { return null; }
};

export const getParentDomain = () => {
  if (typeof window !== 'undefined') {
    return window.location.hostname;
  }
  return 'localhost';
};
